import {createSlice} from '@reduxjs/toolkit';
import omit from 'lodash/omit';
import without from 'lodash/without';
import dayjs from 'utils/dayjs';
import {displayDateFormat} from 'hooks/config';

/** @namespace Reports.Actions */

/**
 * @typedef {object} TReportsSliceInitialState
 * @property {object} filters
 * @property {string} filters.dateFrom - The start date for the report in the format MM/DD/YYYY.  Defaults to the start of the previous month
 * @property {string} filters.dateTo - The end date for the report in the format MM/DD/YYYY.  Defaults to the end of the previous month
 * @property {('all'|'transient'|'monthly')} filters.reservationType - The type of reservation to include in the report.  Defaults to 'all'
 * @property {number} filters.numFiltersApplied - The number of filters that have been applied.  Defaults to 0
 */
export const initialState = {
    filters: {
        dateFrom: dayjs().startOf('month').format(displayDateFormat),
        dateTo: dayjs().format(displayDateFormat),
        reservationType: 'all',
        numFiltersApplied: 0,
    },
};

const reportsSlice = createSlice({
    name: 'reports',
    initialState,
    reducers: {
        /**
         * Updates the filters for the reports
         * @memberof Reports.Actions
         * @param {TReportsSliceInitialState} [state] - The current state
         * @param payload - The new filters
         * @param payload.dateFrom - The start date for the report in the format MM/DD/YYYY.
         * @param payload.dateTo - The end date for the report in the format MM/DD/YYYY.
         * @param payload.reservationType - The type of reservation to include in the report.
         * @returns {dateFrom: string, dateTo: string, reservationType: string, numFiltersApplied: number}
         *
         */
        updateFilters: {
            reducer: (state, {payload}) => {
                state.filters = payload;
            },
            prepare: (payload) => {
                const initialFilters = omit(
                    initialState.filters,
                    'numFiltersApplied'
                );

                const appliedFilters = without(
                    Object.values(payload),
                    ...Object.values(initialFilters)
                );

                return {
                    payload: {
                        ...payload,
                        numFiltersApplied: appliedFilters.length,
                    },
                };
            },
        },
    },
});

export const {updateFilters} = reportsSlice.actions;
export default reportsSlice.reducer;
